import { Box, Text } from '@dtx-company/shared-components/src'
import { GET_UNAUTH_PAGE } from '@dtx-company/flow-codegen/src/page/queries'
import {
  LinkInfoFragment as LinkType,
  UnauthPageQuery,
  UnauthPageQueryVariables
} from '@dtx-company/flow-codegen/src/page/generated.types'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { getValidatedActionData } from '../../components/widgets/typeUtils'
import { pageGqlFetcher } from '@dtx-company/inter-app/src/services/gqlFetcher'
import { removeNullsOnOptional } from '@dtx-company/true-common/src/utils/objects'
import { trackHeapEvent } from '../../utils/heap'
import { useCurrentPage } from '@dtx-company/inter-app/src/redux/slices/utils/currentPage'
import { useRouter } from 'next/router'
import CovidForm from '../../components/covidCheckIn/CovidForm'
import NotFound from '../../components/NotFound'
import React from 'react'

import styled from 'styled-components'
import useSWR from 'swr'

const Link = styled.a`
  text-decoration: none;
`

// TODO: add injected meta - DF

const CovidCheckIn: React.ElementType = () => {
  const router = useRouter()
  const currentPage = useCurrentPage()
  const sentFromProfilePage = Boolean(currentPage) && currentPage?.slugName === router.query.slug
  const host = window?.location && window?.location?.host
  const domainServiceSubdomain = host ? appendProtocol(host) : null
  const { data, error } = useSWR(
    sentFromProfilePage ? null : [GET_UNAUTH_PAGE, (router.query.slug as string)?.toLowerCase()],
    ([query, slug]) =>
      pageGqlFetcher<UnauthPageQuery, UnauthPageQueryVariables>(query, {
        slug,
        domainServiceSubdomain
      })
  )
  const page = sentFromProfilePage ? currentPage : data?.unauthPage
  const checkInLink: LinkType | undefined = removeNullsOnOptional(page?.links)?.find(
    ({ type }) => type === 'covidCheckIn'
  )
  const pushFlowpage: () => void = () => router.push(Routes.FLOWPAGE, '/' + router.query.slug)
  React.useEffect(
    () => {
      if (page && !checkInLink) {
        pushFlowpage()
      }
    }, // TODO: resolve this rule of hooks error - issue tracked in https://app.shortcut.com/flowcode/story/41455/resolve-rule-of-hooks-error-that-were-commented-out
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, checkInLink]
  )
  const actionData = getValidatedActionData<'covidCheckIn'>(checkInLink?.actionData, 'fields')
  const fields = React.useMemo(
    () => (actionData?.fields ? new Set(actionData.fields) : null),
    [actionData]
  )
  const [filledOut, setFilledOut] = React.useState(false)
  if (!sentFromProfilePage && error) {
    return (
      <NotFound
        profileImage={currentPage?.profileImage || ''}
        displayName={currentPage?.profileImage || ''}
        fromFlowpage
      />
    )
  }
  return checkInLink ? (
    <Box
      margin="0 auto"
      height={'fit-content'}
      boxShadow={[null, null, 'float']}
      borderRadius="8px"
      width={[null, null, 'min-content']}
      mt={[null, null, 32]}
      flexDirection="column"
      pl={[null, null, 16]}
      pr={[null, null, 16]}
      pt={32}
    >
      {!filledOut && (
        <>
          <Text variant="body/large" textAlign="center" mb="8px">
            {checkInLink.title}
          </Text>
          <Text variant="body/small" mb="18px" textAlign="center" color="primary.grey">
            {checkInLink.description}
          </Text>
        </>
      )}
      <CovidForm
        fields={fields}
        id={checkInLink.id}
        onFinished={() => {
          setFilledOut(true)
          trackHeapEvent('Submit COVID Form', { link_id: checkInLink.id })
        }}
      />
      <Box width="100%" justifyContent="center" mt={32} mb={[null, null, 16]}>
        <Link href="/termsofservice" target="_blank" rel="noopener noreferrer">
          <Text cursor="pointer" variant="body/small" fontWeight="bold">
            Terms of Service&nbsp;&nbsp;
          </Text>
        </Link>
        <Link href="/privacypolicy" target="_blank" rel="noopener noreferrer">
          <Text cursor="pointer" variant="body/small" fontWeight="bold">
            Privacy Policy
          </Text>
        </Link>
      </Box>
    </Box>
  ) : null
}

export const appendProtocol = (domain: string): string => {
  return `https://${domain.toLowerCase()}/`
}

export const CovidCheckInContainer = CovidCheckIn
